// <infinite-scroll> replaces numbered pagination: when it nears the viewport it fetches data-next-url,
// appends that page's children of #<data-target> to the current list, and takes over the next page's URL.
// The inner [data-infinite-more] link is the manual trigger and no-JS fallback. Rendered by blocks/pagination.liquid.
class InfiniteScroll extends HTMLElement {
  #observer = null;
  #loading = false;

  connectedCallback() {
    this.querySelector('[data-infinite-more]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.#load();
    });
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.#load();
    }, { rootMargin: '800px 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #load() {
    const nextUrl = this.dataset.nextUrl;
    const list = document.getElementById(this.dataset.target);
    if (this.#loading || !nextUrl || !list) return;
    this.#loading = true;
    this.#setStatus(this.dataset.loadingLabel);
    try {
      const response = await fetch(nextUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextList = doc.getElementById(this.dataset.target);
      if (!nextList) throw new Error('List not found on next page');
      const fragment = document.createDocumentFragment();
      for (const child of [...nextList.children]) fragment.append(document.importNode(child, true));
      list.append(fragment);

      const nextLoader = doc.querySelector(`infinite-scroll[data-target="${CSS.escape(this.dataset.target)}"]`);
      const followingUrl = nextLoader?.dataset.nextUrl;
      if (!followingUrl) {
        this.remove();
        return;
      }
      this.dataset.nextUrl = followingUrl;
      this.querySelector('[data-infinite-more]')?.setAttribute('href', followingUrl);
      this.#setStatus('');
      this.#loading = false;
      // Re-check: if the sentinel is still on screen (short pages), keep loading.
      this.#observer.unobserve(this);
      this.#observer.observe(this);
    } catch (error) {
      console.warn('Infinite scroll failed:', error);
      this.#setStatus(this.dataset.errorLabel);
      this.#loading = false;
      this.#observer.disconnect();
    }
  }

  #setStatus(text) {
    const status = this.querySelector('[data-infinite-status]');
    if (status) status.textContent = text || '';
  }
}

if (!customElements.get('infinite-scroll')) customElements.define('infinite-scroll', InfiniteScroll);
